import BaseModal from '@material-ui/core/Modal';
import { GeneralCloseIcon, GeneralMenuIcon } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import OutdoorsyAutoLogo from '@/assets/images/outdoorsy-auto-logo.svg';
import OutdoorsyLogo from '@/assets/images/outdoorsy-logo.svg';
import Anchor from '@/components/switchback/Anchor/Anchor';
import { EVENT_MOBILE_MENU_CLOSE, EVENT_MOBILE_MENU_OPEN } from '@/hooks/useAuthModalState';
import { useRouter } from '@/hooks/useRouter';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { getUser } from '@/redux/selectors/auth/user';
import { trackNavClickedEvent } from '@/services/analytics/header';
import { get2FASessionCookie, reset2FASessionCookie } from '@/utility/session';

import SearchHeader from '../SearchHeader/SearchHeader';
import GlobalHeaderLogoOnly from './GlobalHeaderNav/GlobalHeaderLogoOnly';
import GlobalHeaderNavContainer from './GlobalHeaderNav/GlobalHeaderNavContainer';
import GlobalHeaderNavMobileMenu from './GlobalHeaderNav/GlobalHeaderNavMobileMenu';
import GlobalHeaderNavMyAccountAvatar from './GlobalHeaderNav/GlobalHeaderNavMyAccountAvatar';

interface IProps {
  isHomePage?: boolean;
  noSearchNavbar?: boolean;
  isFluidPage?: boolean;
  isTransparent?: boolean;
  isSticky?: boolean;
  isSERP?: boolean;
  isHeaderWhite?: boolean;
  isDarkNavigation?: boolean;
  isEscapesPage?: boolean;
  isRvDeliveryPage?: boolean;
  isAutoHomePage?: boolean;
  isAutoPage?: boolean;
  isNewPromoPage?: boolean;
  isCheckout?: boolean;
  isRVListingPage?: boolean;
  isCampgroundSearchPage?: boolean;
}

const GlobalHeader: React.FC<IProps> = ({
  isHomePage,
  noSearchNavbar,
  isFluidPage,
  isTransparent,
  isSticky,
  isSERP,
  isHeaderWhite,
  isDarkNavigation,
  isEscapesPage,
  isRvDeliveryPage,
  isAutoHomePage,
  isAutoPage,
  isNewPromoPage,
  isCheckout,
  isRVListingPage,
  isCampgroundSearchPage,
}) => {
  const router = useRouter();
  const user = useSelector(getUser);
  const isAuthenticated = useSelector(selectAuthenticated);

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [stuckToTop, setStuckToTop] = useState(false);

  useEffect(() => {
    if (router.pathname === '/security') {
      const twoFASessionCookie = get2FASessionCookie();
      setRequires2FA(Boolean(twoFASessionCookie && twoFASessionCookie.show_2fa_screen));
      return;
    }

    setRequires2FA(false);
  }, [router.pathname]);

  useEffect(() => {
    if (!isSticky) return setStuckToTop(false);

    const updateStuckToTop = () => setStuckToTop(window.scrollY > 0);

    window.addEventListener('scroll', updateStuckToTop, { passive: true });

    updateStuckToTop();

    return () => {
      window.removeEventListener('scroll', updateStuckToTop);
    };
  }, [isSticky]);

  // Dispatch mobile menu events when state changes
  useEffect(() => {
    if (isNavOpen) {
      document.dispatchEvent(new CustomEvent(EVENT_MOBILE_MENU_OPEN));
    } else {
      document.dispatchEvent(new CustomEvent(EVENT_MOBILE_MENU_CLOSE));
    }
  }, [isNavOpen]);

  const onClickLogo = () => {
    trackNavClickedEvent({
      clickedLink: 'Outdoorsy',
      isInDropdown: false,
    });

    if (requires2FA) {
      reset2FASessionCookie();
    }
  };

  if (requires2FA) {
    if (isNavOpen) {
      setIsNavOpen(false);
    }

    return <GlobalHeaderLogoOnly onClickLogo={onClickLogo} />;
  }

  return (
    <header
      id="global-header"
      data-testid="global-header"
      style={
        isHomePage ||
        isAutoHomePage ||
        isEscapesPage ||
        isRvDeliveryPage ||
        isNewPromoPage ||
        isSERP ||
        isCheckout
          ? { backgroundColor: 'var(--page-header-bg-color)' }
          : {}
      }
      className={cn('z-40', {
        '!bg-transparent': isTransparent && !stuckToTop,
        'md:shadow-500': (isEscapesPage || isAutoHomePage) && stuckToTop,
        'transition-[background-color] duration-700 ease-in-out': isHomePage || isRvDeliveryPage,
        'z-60': isNavOpen || isMobileSearchOpen,
        'absolute left-0 right-0': isTransparent,
        'transition-colors ease-linear':
          isTransparent && isSticky && !isEscapesPage && !isAutoHomePage,
        'bg-transparent text-white': isTransparent && !stuckToTop,
        'bg-canvas-100 text-gray-900 border-b border-gray-200':
          isTransparent && stuckToTop && !isEscapesPage && !isAutoHomePage,
        'relative bg-canvas-100 text-gray-900': !isTransparent,
        'bg-white border-b-0': isHeaderWhite,
      })}>
      <div
        className={cn({
          'grid grid-cols-main container-main gap-x-default':
            !isFluidPage &&
            !isHomePage &&
            !isAutoHomePage &&
            !isEscapesPage &&
            !isRvDeliveryPage &&
            !isNewPromoPage,
          'px-6 max-w-xxxxl lg:px-20 mx-auto':
            isHomePage || isAutoHomePage || isEscapesPage || isRvDeliveryPage || isNewPromoPage,
        })}>
        <div
          className={cn('flex items-center justify-between py-3 col-content lg:py-7 sm:py-4 h-18', {
            [isAutoPage ? 'mx-4' : 'mx-4 xxxl:mx-16']:
              isFluidPage && !isHomePage && !isAutoHomePage && !isEscapesPage && !isRvDeliveryPage,
            'container-main w-full':
              !isFluidPage && !isHomePage && !isAutoHomePage && !isEscapesPage && !isRvDeliveryPage,
            'lg:py-7 sm:py-4': noSearchNavbar,
          })}>
          <Anchor
            href={isAutoPage ? '/auto' : '/'}
            className={cn('mr-4', {
              'hidden xl:block': !noSearchNavbar,
            })}>
            {isAutoPage ? (
              <OutdoorsyAutoLogo
                width={128}
                onClick={onClickLogo}
                className={cn('translate-y-1 transform', {
                  'text-white': isTransparent && !isDarkNavigation && !stuckToTop,
                  'text-gray-900': !isTransparent || stuckToTop,
                })}
              />
            ) : (
              <OutdoorsyLogo
                onClick={onClickLogo}
                className={cn('translate-y-1 transform', {
                  'text-white': isTransparent && !isDarkNavigation && !stuckToTop,
                  'text-gray-900': !isTransparent || stuckToTop,
                })}
              />
            )}
          </Anchor>

          {!noSearchNavbar && !isAutoPage && (
            <SearchHeader
              onCloseMobile={() => setIsMobileSearchOpen(false)}
              onOpenMobile={() => setIsMobileSearchOpen(true)}
              isSERP={isSERP}
              isCampground={isCampgroundSearchPage}
            />
          )}

          <div id="global-header-spacer" className="grow" />

          <button
            className={cn('ml-4 lg:hidden text-gray-900', {
              'rounded-full bg-white w-10 h-10 flex items-center justify-center shrink-0':
                !isAuthenticated,
            })}
            onClick={() => setIsNavOpen(true)}>
            {!isAuthenticated ? (
              <GeneralMenuIcon fontSize={24} />
            ) : (
              <GlobalHeaderNavMyAccountAvatar
                firstName={user?.first_name}
                lastName={user?.last_name}
                avatarUrl={user?.avatar_url}
                isAutoPage={isAutoPage}
                unreadConversations={user?.unread_conversations}
              />
            )}
          </button>

          <div className="hidden lg:block">
            <GlobalHeaderNavContainer
              isHomePage={isHomePage}
              isEscapesPage={isEscapesPage}
              isRvDeliveryPage={isRvDeliveryPage}
              isAutoHomePage={isAutoHomePage}
              isAutoPage={isAutoPage}
              isCheckout={isCheckout}
              isRVListingPage={isRVListingPage}
              noSearchNavbar={noSearchNavbar}
              isTransparent={isTransparent && !isDarkNavigation && !stuckToTop}
              isHeaderWhite={isHeaderWhite}
            />
          </div>

          <div className="lg:hidden">
            <BaseModal open={isNavOpen} onClose={() => setIsNavOpen(false)} disablePortal>
              <div
                style={isHomePage ? { backgroundColor: 'var(--page-header-bg-color)' } : {}}
                className={cn(
                  'absolute inset-0 z-50 flex flex-col items-start',
                  isAutoPage ? 'bg-white' : 'bg-canvas-100',
                  { 'transition-[background-color] duration-700 ease-in-out': isHomePage },
                )}>
                <div className="flex items-start justify-between w-full pb-5 pl-6 pr-4 border-b pt-7 border-gray-310">
                  <Anchor href={isAutoPage ? '/auto' : '/'} className="mr-4">
                    {isAutoPage ? (
                      <OutdoorsyAutoLogo
                        width={128}
                        onClick={onClickLogo}
                        className="text-gray-900"
                      />
                    ) : (
                      <OutdoorsyLogo onClick={onClickLogo} className="text-gray-900" />
                    )}
                  </Anchor>
                  <button className="text-gray-800" onClick={() => setIsNavOpen(false)}>
                    <GeneralCloseIcon fontSize={24} />
                  </button>
                </div>

                <div className="flex-1 w-full px-4 pb-5 overflow-y-auto">
                  <GlobalHeaderNavMobileMenu
                    isAutoPage={isAutoPage}
                    userName={user?.first_name}
                    isOwner={user?.owner}
                    unreadConversations={user?.unread_conversations}
                  />
                </div>
              </div>
            </BaseModal>
          </div>
        </div>
      </div>
    </header>
  );
};

export default GlobalHeader;
